/**
 * Lift2Fit — All Programmes, the dedicated page (`#/programme/all`).
 *
 * The inline list on the Programme page is the SHORT one (name, dates, cadence,
 * actions). Everything the old inline row carried — sessions completed, days and
 * sets logged, the lineage — moved here, where there is room to read it. Nothing
 * was dropped; the Programme page just stopped carrying its history.
 *
 * One page, two audiences: a lifter reads their own blocks and can switch the
 * running one; a coach opens the same component read-only for a linked client
 * with `?clientId=…` (the API already applies the coach-owned scope, so this
 * page never decides who may read what).
 */
import { useState } from 'react';
import { api, type PlanHistoryEntry } from '@/lib/api';
import { useSession } from '@/lib/useSession';
import { AllProgrammesPanel } from '../all-programmes';
import { EmptyState, LoadingPanel, ScreenHeader, TrackBadge } from '../components';
import { useAsync } from '../hooks';
import { Link, useRoute } from '../router';

/** `?clientId=…` off the current route (the router keeps the query now). */
function readClientId(route: string): string | null {
  const query = route.split('?')[1];
  if (!query) return null;
  const value = new URLSearchParams(query).get('clientId');
  return value ? value : null;
}

export function ProgrammeAllScreen() {
  const session = useSession();
  const route = useRoute();
  const clientId = readClientId(route);
  const coachScope = clientId !== null && session.hasRole('coach');
  const historyState = useAsync(() => api.programmePlans(clientId ?? undefined), [clientId ?? 'own']);
  const blocks = historyState.data ?? [];
  const [busyId, setBusyId] = useState<string | null>(null);

  const activate = async (entry: PlanHistoryEntry) => {
    setBusyId(entry.planId);
    await api.activateProgramme(entry.planId, clientId ?? undefined);
    setBusyId(null);
    historyState.reload();
  };

  /** Stop the running block; it stays here to start again. */
  const deactivate = async (entry: PlanHistoryEntry) => {
    setBusyId(entry.planId);
    await api.deactivateProgramme(entry.planId, clientId ?? undefined);
    setBusyId(null);
    historyState.reload();
  };

  /** Delete an inactive block (the API refuses the active one). */
  const remove = async (entry: PlanHistoryEntry) => {
    setBusyId(entry.planId);
    await api.deleteProgrammePlan(entry.planId, clientId ?? undefined);
    setBusyId(null);
    historyState.reload();
  };

  if (historyState.loading && blocks.length === 0) {
    return <LoadingPanel label="Loading your blocks" rows={4} />;
  }

  if (historyState.error && blocks.length === 0) {
    return (
      <EmptyState
        title="We could not read those blocks"
        body={historyState.error}
        action={
          <Link
            to={coachScope ? `/crm/clients/${encodeURIComponent(clientId ?? '')}` : '/programme'}
            className="inline-flex h-11 items-center border border-hairline px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
          >
            Back
          </Link>
        }
      />
    );
  }

  if (coachScope) {
    return (
      <div className="flex flex-col gap-6" data-testid="programme-all">
        <ScreenHeader
          eyebrow="Coaching"
          title="All Programmes"
          lead="Every block you wrote for this client, with what each one produced. Read-only: the client chooses which block runs."
          actions={
            <Link
              to={`/crm/clients/${encodeURIComponent(clientId ?? '')}`}
              data-testid="programme-all-back"
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
            >
              Back to the client
            </Link>
          }
        />
        <AllProgrammesPanel
          audience="coach"
          blocks={blocks}
          loading={historyState.loading}
          subtitle="Every block you wrote for this client, newest first. The record of what ran stays intact."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="programme-all">
      <ScreenHeader
        eyebrow="Your training"
        title="All Programmes"
        badge={blocks[0] ? <TrackBadge track={blocks[0].track} /> : undefined}
        lead="Every block you have had, with what each one produced. Switch back to any of them here; the record of what ran stays intact."
        actions={
          <Link
            to="/programme"
            data-testid="programme-all-back"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
          >
            Back to my programme
          </Link>
        }
      />
      <AllProgrammesPanel
        audience="lifter"
        blocks={blocks}
        loading={historyState.loading}
        busyId={busyId}
        onActivate={(entry) => void activate(entry)}
        onDeactivate={(entry) => void deactivate(entry)}
        onDelete={(entry) => void remove(entry)}
      />
    </div>
  );
}
